// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

/** HTTP status codes */
export enum HttpStatusCode {
    Ok = 200,
    Created = 201,
    Accepted = 202,
    NoContent = 204,
    BadRequest = 400,
    Unauthorized = 401,
    Forbidden = 403,
    NotFound = 404,
    NotAcceptable = 406,
    Conflict = 409,
    InternalServerError = 500,
}

/** HTTP error codes. */
export type HttpErrorCode = HttpStatusCode.BadRequest | HttpStatusCode.NotFound;

/** Returns whether given status code represents a success response. */
export function isHttpSuccess(statusCode: number): boolean {
    return statusCode >= 200 && statusCode < 300;
}

/** Returns whether given error object represents a specific HTTP status code. */
export function isHttpError(e: unknown, statusCode: HttpStatusCode): boolean {
    return (
        typeof e === 'object' &&
        e !== null &&
        (('statusCode' in e && e.statusCode === statusCode) || ('code' in e && e.code === statusCode))
    );
}

/**
 * Returns whether given error object represents an HTTP Not Found (404) error.
 * Supports Azure SDK and Cosmos SDK error objects.
 */
export function isNotFoundError(e: unknown): boolean {
    return isHttpError(e, HttpStatusCode.NotFound);
}

/** HTTP error class. */
export class HttpError extends Error {
    constructor(
        message: string,
        public statusCode: HttpStatusCode,
        public code: string,
        public expose = true
    ) {
        super(message);
    }
}

/** Throws specified HTTP error. */
export function throwHttpError(statusCode: HttpErrorCode, message?: string, code?: string): never {
    switch (statusCode) {
        case HttpStatusCode.BadRequest:
            message ??= 'Invalid request body or parameters.';
            code ??= 'BadRequest';
            break;
        case HttpStatusCode.NotFound:
            message ??= 'Resource not found.';
            code ??= 'NotFound';
            break;
    }

    throw new HttpError(message, statusCode, code);
}
